import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import {
  TRANSLATION_DOC_LANDINGS,
  getTranslationDocLanding,
  type TranslationDocLanding,
} from "@/content/translation-corpus/documents";

function faqsEn(d: TranslationDocLanding) {
  return [
    { q: `Which authorities accept ${d.titleEn}?`, a: d.useCaseEn },
    { q: "What do I need to send you?", a: d.prepareEn.join(" · ") },
    { q: "Which certification system applies?", a: d.certSystemEn },
    { q: "Why do translations of this document get rejected?", a: d.pitfallsEn.join(" · ") },
    { q: "What happens after the translation is certified?", a: d.nextStepEn },
    { q: "How long does it take?", a: `${d.turnaroundEn} Pricing depends on page count and certification tier; a free quote is available before any work starts.` },
    {
      q: "What is the difference between translation, certification, notarisation and legalisation?",
      a: "They are four separate layers: (1) the translation itself, (2) the translator's certificate of accuracy, (3) notarisation of the signer's signature by a notarial services attorney, and (4) legalisation or apostille, which authenticates seals for cross-border use. None of the first three replaces the fourth.",
    },
    {
      q: "Can Thailand issue an apostille yet?",
      a: "Not yet in practice. Thailand deposited its instrument of accession to the Hague Apostille Convention on 30 June 2026 and the Convention enters into force for Thailand on 28 February 2027. Until then, Thai documents follow the Department of Consular Affairs legalisation route followed by destination embassy attestation. Check hcch.net and consular.mfa.go.th for the current status.",
    },
    {
      q: "Do you guarantee the receiving authority will accept it?",
      a: "Acceptance is always the receiving authority's decision, so no outcome can be guaranteed. We confirm the exact certification system your destination requires before starting, and we redo the work free of charge if the error is on our side.",
    },
  ];
}

export const Route = createFileRoute("/en/translation/document/$doc")({
  loader: ({ params }: { params: { doc: string } }) => {
    const d = getTranslationDocLanding(params.doc);
    if (!d) throw notFound();
    return { d };
  },
  component: TranslationDocPageEN,
  head: ({ params, loaderData }: { params: { doc: string }; loaderData?: { d: TranslationDocLanding } }) => {
    if (!loaderData) {
      return { meta: [{ title: "Document type not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { d } = loaderData;
    const title = `${d.titleEn} (2026)`;
    const description = `${d.titleEn}: what to prepare, the certification system each destination requires, legalisation steps and turnaround. ${d.turnaroundEn}`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/en/translation/document/${params.doc}`,
        type: "article",
        alternates: [{ lang: "th", path: `/translation/document/${params.doc}` }],
        keywords: [d.titleEn, "certified translation Thailand", "MFA legalisation Thailand", "sworn translation", "NAATI translation"],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Certified Translation", url: `${site}/en/translation` },
              { name: d.titleEn, url: `${site}/en/translation/document/${params.doc}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqsEn(d))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/translation/document/${params.doc}`)) },
      ],
    };
  },
});

function TranslationDocPageEN() {
  const { d } = Route.useLoaderData();
  const faqs = faqsEn(d);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/translation/faq" className="hover:underline">Certified Translation</a> <span>/</span>{" "}
          <span>{d.titleEn}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{d.titleEn} (2026)</h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">
          Source document: <strong>{d.officialEn}</strong> — certification system: {d.certSystemEn}
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">Get a free quote →</a>
          <a href="/en/translation/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">Translation FAQ</a>
          <a href={`/translation/document/${d.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">ภาษาไทย →</a>
        </div>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">What it is used for</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{d.useCaseEn}</p>
        </section>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">What to prepare</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.prepareEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
          <p className="mt-4 text-sm text-muted-foreground"><strong>Turnaround:</strong> {d.turnaroundEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Why these translations get rejected</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.pitfallsEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">Next step after certification</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{d.nextStepEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">FAQ — {d.titleEn}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Other document types</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {TRANSLATION_DOC_LANDINGS.filter((x) => x.slug !== d.slug).map((x) => (
              <a key={x.slug} href={`/en/translation/document/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleEn}
              </a>
            ))}
          </div>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          Information current as of August 2026. Legalisation steps per the Department of Consular Affairs, MFA
          (consular.mfa.go.th); Apostille Convention status per hcch.net; Australian translator certification per
          naati.com.au. Always confirm requirements with the receiving authority before proceeding.
        </p>
      </main>
      <SiteFooter />
    </>
  );
}